// src/pages/ViajesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const ESTADO_COLORS = {
  programado: { bg: '#e6f7ff', color: '#0050b3', label: '🗓 Programado' },
  en_ruta:    { bg: '#f6ffed', color: '#135200', label: '🚌 En Ruta' },
  finalizado: { bg: '#f0f0f0', color: '#595959', label: '✅ Finalizado' },
  cancelado:  { bg: '#fff1f0', color: '#a8071a', label: '❌ Cancelado' },
};

export default function ViajesPage() {
  const { isAdmin } = useAuth();
  const [viajes, setViajes] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedViaje, setSelectedViaje] = useState(null);
  const [pasajeros, setPasajeros] = useState([]);
  const [filters, setFilters] = useState({ fecha_inicio: '', fecha_fin: '', estado: '', tipo_viaje: '' });

  const [form, setForm] = useState({
    placa_bus: '', conductor1: '', conductor2: '', fecha_viaje: '', hora_viaje: '',
    tipo_viaje: 'subida', destino: 'Unidad Minera', id_provincia_origen: '',
  });

  const [showCheckinForm, setShowCheckinForm] = useState(false);
  const [checkinDni, setCheckinDni] = useState('');
  const [checkinWorker, setCheckinWorker] = useState(null);
  const [searchingDni, setSearchingDni] = useState(false);

  const fetchViajes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)));
      const res = await api.get(`/viajes?${params}`);
      setViajes(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get('/provincias?activas=1').then(r => setProvincias(r.data.data));
  }, []);

  useEffect(() => { fetchViajes(); }, [fetchViajes]);

  const fetchPasajeros = async (idViaje) => {
    const res = await api.get(`/viajes/${idViaje}/pasajeros`);
    setPasajeros(res.data.data);
  };

  const handleSelectViaje = (v) => {
    setSelectedViaje(v);
    fetchPasajeros(v.id);
  };

  const handleEditClick = () => {
    if (!selectedViaje) {
      alert('Por favor seleccione un viaje primero');
      return;
    }
    setForm({
      placa_bus: selectedViaje.placa_bus || '',
      conductor1: selectedViaje.conductor1 || '',
      conductor2: selectedViaje.conductor2 || '',
      fecha_viaje: selectedViaje.fecha_viaje ? selectedViaje.fecha_viaje.split('T')[0] : '',
      hora_viaje: selectedViaje.hora_viaje || '',
      tipo_viaje: selectedViaje.tipo_viaje || 'subida',
      destino: selectedViaje.destino || 'Unidad Minera',
      id_provincia_origen: selectedViaje.origen_id || '',
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedViaje) {
        await api.put(`/viajes/${selectedViaje.id}`, form);
      } else {
        await api.post('/viajes', form);
      }
      setShowForm(false);
      setIsEditing(false);
      setForm({ placa_bus:'',conductor1:'',conductor2:'',fecha_viaje:'',hora_viaje:'',tipo_viaje:'subida',destino:'Unidad Minera',id_provincia_origen:'' });
      fetchViajes();
      if (isEditing && selectedViaje) {
        handleSelectViaje(selectedViaje);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar');
    }
  };

  const handleCheckinSubmit = async (e, forzarRegistro = false) => {
    if (e) e.preventDefault();
    if (checkinDni.length !== 8) return;
    
    try {
      const res = await api.post(`/viajes/${selectedViaje.id}/checkin`, { dni: checkinDni, forzar_registro: forzarRegistro });
      setCheckinWorker(res.data.pasajero);
      setCheckinDni('');
      fetchPasajeros(selectedViaje.id);
      fetchViajes();
    } catch (err) {
      if (err.response?.data?.not_found) {
        if (window.confirm('El DNI ingresado no está registrado. ¿Desea registrarlo como "Sin Registro"?')) {
          handleCheckinSubmit(null, true);
        }
      } else {
        alert(err.response?.data?.message || 'Error al registrar pasajero');
      }
    }
  };

  const cambiarEstado = async (id, estado) => {
    await api.patch(`/viajes/${id}/estado`, { estado });
    fetchViajes();
    if (selectedViaje?.id === id) setSelectedViaje(v => ({ ...v, estado }));
  };

  const exportarPasajeros = () => {
    if (!pasajeros.length) return;
    const ws = XLSX.utils.json_to_sheet(pasajeros.map(p => ({
      DNI: p.dni_pasajero, 'Apellido Paterno': p.ape_paterno, 'Apellido Materno': p.ape_materno,
      Nombres: p.nombres, Sexo: p.sexo, Área: p.area, Cargo: p.cargo,
      'Nº Asiento': p.numero_asiento, Abordaje: p.lugar_abordaje,
      'Check-In': p.check_in ? 'SÍ' : 'NO', 'Fecha Check-In': p.fecha_check_in,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pasajeros');
    XLSX.writeFile(wb, `pasajeros_viaje_${selectedViaje?.id}_${Date.now()}.xlsx`);
  };

  const s = {
    title: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 20 },
    card: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,.08)' },
    filters: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
    input: { padding: '7px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13 },
    select: { padding: '7px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, background: '#fff' },
    btn: (color='#d4940a') => ({ padding: '8px 16px', background: color, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }),
    row: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 },
    badge: (est) => ({ ...ESTADO_COLORS[est], padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }),
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    formInput: { width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    formLabel: { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' },
    checkBadge: (ok) => ({ display:'inline-block', width:10, height:10, borderRadius:'50%', background: ok ? '#52c41a' : '#d9d9d9', marginRight: 6 }),
  };

  return (
    <div>
      <div className="responsive-header">
        <div>
          <div style={s.title}>🚌 Gestión de Viajes</div>
          <div style={{ color: '#888', fontSize: 13 }}>Registro y seguimiento de viajes de personal</div>
        </div>
      </div>

      {/* Formulario nuevo viaje */}
      {showForm && (
        <div style={{ ...s.card, marginBottom: 16, borderLeft: '4px solid #d4940a' }}>
          <h3 style={{ marginTop: 0 }}>{isEditing ? 'Editar Viaje' : 'Nuevo Viaje'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="responsive-grid-2">
              <div><label style={s.formLabel}>Placa Bus *</label>
                <input style={s.formInput} value={form.placa_bus} onChange={e=>setForm({...form,placa_bus:e.target.value})} required placeholder="ABC-123" /></div>
              <div><label style={s.formLabel}>Conductor 1 *</label>
                <input style={s.formInput} value={form.conductor1} onChange={e=>setForm({...form,conductor1:e.target.value})} required /></div>
              <div><label style={s.formLabel}>Conductor 2</label>
                <input style={s.formInput} value={form.conductor2} onChange={e=>setForm({...form,conductor2:e.target.value})} /></div>
              <div><label style={s.formLabel}>Fecha *</label>
                <input type="date" style={s.formInput} value={form.fecha_viaje} onChange={e=>setForm({...form,fecha_viaje:e.target.value})} required /></div>
              <div><label style={s.formLabel}>Hora *</label>
                <input type="time" style={s.formInput} value={form.hora_viaje} onChange={e=>setForm({...form,hora_viaje:e.target.value})} required /></div>
              <div><label style={s.formLabel}>Tipo *</label>
                <select style={s.formInput} value={form.tipo_viaje} onChange={e=>setForm({...form,tipo_viaje:e.target.value})}>
                  <option value="subida">🔼 Subida</option>
                  <option value="bajada">🔽 Bajada</option>
                </select></div>
              <div><label style={s.formLabel}>Provincia Origen *</label>
                <select style={s.formInput} value={form.id_provincia_origen} onChange={e=>setForm({...form,id_provincia_origen:e.target.value})} required>
                  <option value="">Seleccione...</option>
                  {provincias.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select></div>
              <div><label style={s.formLabel}>Destino</label>
                <input style={s.formInput} value={form.destino} onChange={e=>setForm({...form,destino:e.target.value})} /></div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button type="submit" style={s.btn()}>💾 Guardar</button>
              <button type="button" style={s.btn('#888')} onClick={() => { setShowForm(false); setIsEditing(false); }}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros */}
      <div style={s.filters}>
        <input type="date" style={s.input} value={filters.fecha_inicio} onChange={e=>setFilters({...filters,fecha_inicio:e.target.value})} />
        <input type="date" style={s.input} value={filters.fecha_fin} onChange={e=>setFilters({...filters,fecha_fin:e.target.value})} />
        <select style={s.select} value={filters.estado} onChange={e=>setFilters({...filters,estado:e.target.value})}>
          <option value="">Todos los estados</option>
          <option value="programado">Programado</option>
          <option value="en_ruta">En Ruta</option>
          <option value="finalizado">Finalizado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <select style={s.select} value={filters.tipo_viaje} onChange={e=>setFilters({...filters,tipo_viaje:e.target.value})}>
          <option value="">Todos los tipos</option>
          <option value="subida">Subida</option>
          <option value="bajada">Bajada</option>
        </select>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
            <button style={s.btn('#1890ff')} onClick={handleEditClick}>✏ Editar Viaje</button>
            <button style={s.btn()} onClick={() => { setIsEditing(false); setForm({ placa_bus:'',conductor1:'',conductor2:'',fecha_viaje:'',hora_viaje:'',tipo_viaje:'subida',destino:'Unidad Minera',id_provincia_origen:'' }); setShowForm(true); }}>+ Nuevo Viaje</button>
          </div>
        )}
      </div>

      <div className="responsive-grid-viajes">
        {/* Lista de viajes */}
        <div style={s.card}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>
            Lista de Viajes {loading ? '⏳' : `(${viajes.length})`}
          </h3>
          <div style={{ maxHeight: 500, overflow: 'auto' }}>
            {viajes.map(v => (
              <div key={v.id}
                style={{ ...s.row, background: selectedViaje?.id === v.id ? '#fffbe6' : 'transparent' }}
                onClick={() => handleSelectViaje(v)}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{v.placa_bus} – {v.tipo_viaje === 'subida' ? '🔼' : '🔽'} {v.tipo_viaje.toUpperCase()}</div>
                  <div style={{ color: '#888', fontSize: 12 }}>{v.fecha_viaje?.split('T')[0]} {v.hora_viaje} · {v.provincia_origen}</div>
                </div>
                <span style={{ marginLeft: 'auto' }}>
                  <span style={s.badge(v.estado)}>{ESTADO_COLORS[v.estado]?.label}</span>
                </span>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {v.pasajeros_abordados}/{v.total_pasajeros} 👷
                </div>
              </div>
            ))}
            {!loading && !viajes.length && <div style={{ padding: 20, textAlign: 'center', color: '#aaa' }}>No hay viajes</div>}
          </div>
        </div>

        {/* Detalle viaje seleccionado */}
        <div style={s.card}>
          {selectedViaje ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0 }}>🚌 {selectedViaje.placa_bus}</h3>
                  <div style={{ color: '#888', fontSize: 13 }}>
                    {selectedViaje.tipo_viaje === 'subida' ? '🔼 Subida' : '🔽 Bajada'} · {selectedViaje.provincia_origen} → {selectedViaje.destino}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                  <button style={s.btn('#52c41a')} onClick={exportarPasajeros}>⬇ Excel</button>
                  {isAdmin && <button style={s.btn('#1890ff')} onClick={() => setShowCheckinForm(true)}>➕ Registrar</button>}
                </div>
              </div>

              {/* Cambio de estado */}
              {isAdmin && (
                <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['programado','en_ruta','finalizado','cancelado'].map(est => (
                    <button key={est} style={{ ...s.btn(est===selectedViaje.estado?'#d4940a':'#d9d9d9'), color: est===selectedViaje.estado?'#fff':'#555', padding:'4px 10px', fontSize:11 }}
                      onClick={() => cambiarEstado(selectedViaje.id, est)}>
                      {ESTADO_COLORS[est].label}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                <b>Conductores:</b> {selectedViaje.conductor1}{selectedViaje.conductor2 ? ` / ${selectedViaje.conductor2}` : ''}<br />
                <b>Abordados:</b> <span style={{ color: '#52c41a', fontWeight: 700 }}>{selectedViaje.pasajeros_abordados}</span> / {selectedViaje.total_pasajeros}
              </div>

              <div className="responsive-table-container" style={{ maxHeight: 380 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      {['#','DNI','Apellidos y Nombres','Área','Cargo','Check-In'].map(h => (
                        <th key={h} style={{ padding: '8px 6px', textAlign: 'left', borderBottom: '1px solid #e8e8e8', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pasajeros.map((p, i) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '6px' }}>{i + 1}</td>
                        <td style={{ padding: '6px' }}>{p.dni_pasajero}</td>
                        <td style={{ padding: '6px' }}>{p.ape_paterno} {p.ape_materno}, {p.nombres}</td>
                        <td style={{ padding: '6px', color: '#666' }}>{p.area}</td>
                        <td style={{ padding: '6px', color: '#666' }}>{p.cargo}</td>
                        <td style={{ padding: '6px' }}>
                          <span style={s.checkBadge(p.check_in)}></span>
                          {p.check_in ? <span style={{ color: '#52c41a' }}>✓</span> : <span style={{ color: '#bbb' }}>–</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!pasajeros.length && <div style={{ padding: 20, textAlign: 'center', color: '#aaa' }}>Sin pasajeros registrados</div>}
              </div>
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
              <div style={{ fontSize: 40 }}>🚌</div>
              <div>Selecciona un viaje para ver el detalle</div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Registrar Pasajero */}
      {showCheckinForm && selectedViaje && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{ ...s.card, width: 400, maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>Registrar Pasajero</h3>
            
            <div style={{ marginBottom: 15, fontSize: 13, background: '#f9f9f9', padding: 10, borderRadius: 6 }}>
              <div><b>Placa:</b> {selectedViaje.placa_bus}</div>
              <div><b>Fecha/Hora:</b> {selectedViaje.fecha_viaje?.split('T')[0]} {selectedViaje.hora_viaje}</div>
              <div><b>Ruta:</b> {selectedViaje.provincia_origen} → {selectedViaje.destino}</div>
            </div>

            <form onSubmit={(e) => handleCheckinSubmit(e, false)}>
              <div style={{ marginBottom: 15 }}>
                <label style={s.formLabel}>DNI del Trabajador *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    style={s.formInput} 
                    value={checkinDni} 
                    onChange={e => setCheckinDni(e.target.value)} 
                    maxLength={8}
                    required 
                    placeholder="Ingrese DNI y presione Enter" 
                    autoFocus
                  />
                  <button type="submit" style={s.btn('#1890ff')}>
                    Registrar
                  </button>
                </div>
              </div>

              {checkinWorker && (
                <div style={{ marginBottom: 15, fontSize: 13, padding: 10, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, color: '#135200' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>✅ Registrado Exitosamente:</div>
                  <div><b>Nombres:</b> {checkinWorker.ape_paterno} {checkinWorker.ape_materno}, {checkinWorker.nombres}</div>
                  <div><b>Área:</b> {checkinWorker.area || '-'}</div>
                  <div><b>Cargo:</b> {checkinWorker.cargo || '-'}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" style={s.btn('#888')} onClick={() => { setShowCheckinForm(false); setCheckinWorker(null); setCheckinDni(''); }}>Cerrar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
